/**
 * Printing and exporting one person's tasks. Two outputs, deliberately different
 * shapes:
 *
 *   PRINT  grouped, with a tick column, for working through on paper
 *   EXCEL  one flat table with a Cadence column, so it sorts, filters and pivots
 *
 * The printed page is composed from scratch into #tm-printsheet and is the ONLY
 * thing that goes to the printer — the app is hidden at #root by the @media print
 * block in signalbox.css. Printing the live board instead would mean suppressing
 * the pills, filters, tiles and buttons, and would break again on every layout
 * change.
 */
import { fmtDate, STATUS_LABEL, daysBetween, type MineRow } from './status'

const esc = (s: unknown): string =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

const hhmm = (d: Date): string =>
  `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`

interface Section {
  heading: string
  columns: string[]
  rows: string[][]
}

function printSheet({
  title,
  sub,
  sections,
  today,
}: {
  title: string
  sub: string
  sections: Section[]
  today: Date
}): void {
  const sheet = document.getElementById('tm-printsheet')
  if (!sheet) return
  const stamp = `${fmtDate(today)} at ${hhmm(new Date())}`

  let h = `<div class="tm-ps-head">
      <div><h1>${esc(title)}</h1><p>${esc(sub)}</p></div>
      <div class="tm-ps-meta">Signalbox<br />NYMR Finance<br />Printed ${stamp}</div>
    </div>`

  for (const s of sections) {
    h += `<h2>${esc(s.heading)}</h2>`
    if (!s.rows.length) {
      h += '<p class="tm-ps-empty">Nothing assigned.</p>'
      continue
    }
    h +=
      '<table><thead><tr><th class="tm-ps-tick">Done</th>' +
      s.columns.map((c) => `<th>${esc(c)}</th>`).join('') +
      '</tr></thead><tbody>' +
      s.rows
        .map(
          (r) =>
            '<tr><td class="tm-ps-tick"><span class="tm-ps-box"></span></td>' +
            r.map((c) => `<td>${esc(c)}</td>`).join('') +
            '</tr>'
        )
        .join('') +
      '</tbody></table>'
  }

  const total = sections.reduce((n, s) => n + s.rows.length, 0)
  h +=
    `<p class="tm-ps-foot">${total} task${total === 1 ? '' : 's'} listed. ` +
    `Signalbox is the record; this sheet is a copy taken on ${stamp}.</p>`

  sheet.innerHTML = h
  window.print()
}

/** What one person still has outstanding, with real dates. */
export function printOutstanding({
  name,
  rows,
  today,
}: {
  name: string
  rows: MineRow[]
  today: Date
}): void {
  printSheet({
    title: `Outstanding tasks for ${name}`,
    sub: 'Everything not yet done, soonest first, across the timetable and every programme.',
    sections: [
      {
        heading: 'Outstanding',
        columns: ['Task', 'Period', 'Due', 'Status'],
        rows: rows.map((r) => [
          r.name,
          `${r.cadenceLabel} ${r.periodLabel}`,
          fmtDate(r.due),
          STATUS_LABEL[r.status],
        ]),
      },
    ],
    today,
  })
}

const csvCell = (v: unknown): string => {
  const s = String(v ?? '')
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

export function downloadCsv(filename: string, rows: unknown[][]): void {
  /* The BOM matters: without it Excel reads the file as ANSI and every en-dash
     and pound sign in a task name arrives as mojibake. Written as an escape,
     never a literal — a literal is invisible in the source and does not survive
     a re-save in another encoding. */
  const body = '﻿' + rows.map((r) => r.map(csvCell).join(',')).join('\r\n') + '\r\n'
  const blob = new Blob([body], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

const fileStamp = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

export function exportOutstanding({
  name,
  rows,
  today,
}: {
  name: string
  rows: MineRow[]
  today: Date
}): void {
  const out: unknown[][] = [
    ['Person', 'Cadence', 'Period', 'Task', 'Due', 'Days', 'Status', 'Reference'],
  ]
  for (const r of rows) {
    out.push([
      name,
      r.cadenceLabel,
      r.periodLabel,
      r.name,
      fmtDate(r.due),
      daysBetween(today, r.due),
      STATUS_LABEL[r.status],
      r.ref ?? '',
    ])
  }
  downloadCsv(`Signalbox - ${name} - outstanding - ${fileStamp(today)}.csv`, out)
}
